import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { GET_BRAND_DATA_LIST } from "../../../Constants/Config";
import { ciUrl } from "../../../Constants/nodeCi";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";

const PAGE_LIMIT = 10;

// Shared across reload + loadMore + search so only the latest in-flight list
// request survives — a stale earlier response can't overwrite a newer one.
let cancelBrandTagListSource;

const initialState = {
  loading: false,
  loadingMore: false,
  list: [],
  offset: 0,
  limit: PAGE_LIMIT,
  hasMore: true,
  totalCount: { brand: 0, tag: 0 },
  assignedCount: { brand: 0, tag: 0 },
  totalProductCount: { brand: 0, tag: 0 },
  error: "",
};

export const fetchBrandTagList = createAsyncThunk(
  "brandTagList/fetchBrandTagList",
  async (data, { rejectWithValue }) => {
    // Cancel the previous in-flight request before firing a new one.
    if (cancelBrandTagListSource) {
      cancelBrandTagListSource.cancel("Previous request canceled");
    }
    cancelBrandTagListSource = axios.CancelToken.source();
    const { token, offset = 0, limit = PAGE_LIMIT, ...dataNew } = data;
    try {
      const response = await axios.post(
        ciUrl(GET_BRAND_DATA_LIST),
        { ...dataNew, offset, limit },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          cancelToken: cancelBrandTagListSource.token,
        },
      );

      if (response?.data?.status === true) {
        return {
          data: Array.isArray(response.data.data) ? response.data.data : [],
          offset,
          limit,
          type: dataNew.type,
          totalCount: response.data.total_count || { brand: 0, tag: 0 },
          assignedCount: response.data.assigned_count || { brand: 0, tag: 0 },
          totalProductCount: response.data.total_product_count || {
            brand: 0,
            tag: 0,
          },
        };
      }

      return rejectWithValue(response?.data?.message || "Failed to fetch list");
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ cancelled: true });
      }
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  },
);

const brandTagListSlice = createSlice({
  name: "brandTagList",
  initialState,
  reducers: {
    resetBrandTagList: (state) => {
      state.loading = false;
      state.loadingMore = false;
      state.list = [];
      state.offset = 0;
      state.hasMore = true;
      state.totalCount = { brand: 0, tag: 0 };
      state.assignedCount = { brand: 0, tag: 0 };
      state.totalProductCount = { brand: 0, tag: 0 };
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBrandTagList.pending, (state, action) => {
      const offset = Number(action.meta?.arg?.offset ?? 0);
      if (offset === 0) {
        state.loading = true;
      } else {
        state.loadingMore = true;
      }
      state.error = "";
    });
    builder.addCase(fetchBrandTagList.fulfilled, (state, action) => {
      const { data, offset, limit, type, totalCount, assignedCount, totalProductCount } =
        action.payload;
      const reqOffset = Number(offset ?? 0);
      const isBrand = String(type) === "1";

      state.loading = false;
      state.loadingMore = false;
      state.totalCount = totalCount;
      state.assignedCount = assignedCount;
      state.totalProductCount = totalProductCount;

      if (reqOffset === 0) {
        state.list = data;
      } else {
        state.list = [...state.list, ...data];
      }

      state.offset = reqOffset + data.length;
      const totalItems =
        Number(isBrand ? totalCount?.brand : totalCount?.tag) || 0;
      // Page-fullness is the primary "more pages?" signal: a page shorter than
      // the requested limit means the server has nothing left to give. This is
      // backend-agnostic and fixes search, where total_count reflects the
      // UNFILTERED set and would otherwise keep hasMore=true forever — causing
      // the IntersectionObserver to fire loadMore on repeat. totalItems is only
      // used as an extra upper bound when the server actually provides it.
      const reqLimit = Number(limit) || PAGE_LIMIT;
      const fullPage = data.length >= reqLimit;
      state.hasMore =
        fullPage &&
        data.length > 0 &&
        (totalItems === 0 || state.list.length < totalItems);
    });
    builder.addCase(fetchBrandTagList.rejected, (state, action) => {
      // Superseded by a newer request — leave state to that newer request.
      if (action.payload?.cancelled) return;
      const offset = Number(action.meta?.arg?.offset ?? 0);
      state.loading = false;
      state.loadingMore = false;
      if (offset === 0) {
        state.list = [];
      }
      state.hasMore = false;
      state.error =
        action.payload?.message || action.error?.message || "An error occurred";
    });
  },
});

export const { resetBrandTagList } = brandTagListSlice.actions;
export default brandTagListSlice.reducer;
